import { useRef, useState, useCallback } from 'react'
import { format } from 'date-fns/format'
import cx from '@ui-components-3/ui/lib/utils/classNames'
import Tooltip from '@ui-components-3/ui/lib/components/Tooltip'

import ContentBlock from '../ContentBlock'
import Row from '../Row'
import IconWrapper from '../IconWrapper'

import truncateText from 'util/truncateText'
import VisitModalityIcon, { VISIT_MODALITY } from '../VisitModalityIcon'
import LocationDotIcon from '@ui-components-3/icons/lib/solid/LocationDot'
import AslInterpreterPill from 'components/common/AslInterpreterPill'

type EventSection = {
  date: Date
  visitReason: string
  fullName: string
  age: number
  location: string
  modality: VISIT_MODALITY
  className?: string
  showAslPill?: boolean
}

const MAXIMUM_ROW_LAYOUT_WIDTH = 335

const SettersMap = new WeakMap<Element, (isRow: boolean) => void>()

const RO = new ResizeObserver((entries) => {
  window.requestAnimationFrame(() => {
    if (!Array.isArray(entries) || !entries.length) {
      return
    }
    for (const entry of entries) {
      const setter = SettersMap.get(entry.target)
      if (setter) {
        setter(entry.borderBoxSize[0].inlineSize > MAXIMUM_ROW_LAYOUT_WIDTH)
      }
    }
  })
})

const EventSection = ({
  date,
  visitReason,
  fullName,
  age,
  location,
  modality,
  className,
  showAslPill,
}: EventSection) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [isRow, setRow] = useState(true)
  const isTruncated = visitReason.length >= 50
  const cbRef = useCallback((el: HTMLDivElement | null) => {
    if (ref.current) {
      RO.unobserve(ref.current)
      SettersMap.delete(ref.current)
    }

    if (el) {
      ref.current = el
      SettersMap.set(el, setRow)
      RO.observe(el)
    }
  }, [])

  const renderReason = () => {
    const component = (
      <p className="max-w-[8.57em] text-primary-800 typography-body">
        {truncateText({
          text: visitReason,
          chartsLimit: 50,
          stringsLimit: 5,
          /* TODO: debug, el should not be null(maybe) */
          el: null,
        })}
      </p>
    )

    if (!isTruncated) return component

    return <Tooltip content={visitReason}>{component}</Tooltip>
  }

  return (
    <div className={className}>
      <div className={cx('flex', isRow ? 'flex-row' : 'flex-col')} ref={cbRef}>
        <ContentBlock>
          <p className="typography-caption text-neutral-500">{format(date, 'eeee')}</p>
          <p className="typography-body-xxl text-[32px]">{format(date, 'MMM dd')}</p>
          <p className="typography-caption text-neutral-500">{format(date, 'y')}</p>
        </ContentBlock>
        <ContentBlock>
          <p className="typography-caption text-neutral-500">time</p>
          <p className="typography-body-xxl text-[32px]">{format(date, 'hh:mm')}</p>
          <p className="typography-caption text-neutral-500">{format(date, 'aaa')}</p>
        </ContentBlock>
        <ContentBlock>
          <p className="typography-caption text-neutral-500">reason</p>
          {renderReason()}
        </ContentBlock>
      </div>

      <Row>
        <IconWrapper className="h-[1em]">
          <VisitModalityIcon modality={modality} />
        </IconWrapper>
        <p className="typography-body-l text-primary-800">
          {fullName}, {age} y.o.
        </p>
      </Row>
      <div className="flex items-center justify-between gap-x-4 gap-y-2 flex-wrap">
        <Row grey>
          <LocationDotIcon className="w-5 h-5 !fill-neutral-300" aria-hidden="true" />
          <p className="typography-body-l text-primary-800">{location}</p>
        </Row>
        {!!showAslPill && <AslInterpreterPill />}
      </div>
    </div>
  )
}

export default EventSection
